/* eslint-disable no-console */
/**
 * Modale bloquante de mise à jour.
 *
 * Volontairement en DOM natif, sans Vue ni CSS applicatif :
 *  - elle doit pouvoir s'afficher AVANT le montage de l'application, et même
 *    si ce montage échoue (c'est justement le cas d'un bundle périmé) ;
 *  - elle ne doit dépendre d'aucune feuille de style susceptible d'être servie
 *    depuis un cache invalide. Tous les styles sont donc en ligne.
 *
 * Bloquante et sans échappatoire, par décision produit : la mise à jour est
 * obligatoire. Un seul bouton, « Installer ». Ni croix, ni « plus tard », ni
 * fermeture au clic extérieur ou à la touche Échap.
 */

import { appliquerMiseAJour } from '@/service/appVersionService';

const ID_RACINE = 'hsig-update-gate';
const COULEUR = '#4DBA87';

let affichee = false;

function empecherInteractionArrierePlan(actif) {
    try {
        document.documentElement.style.overflow = actif ? 'hidden' : '';
        document.body.style.overflow = actif ? 'hidden' : '';
    } catch (e) { /* DOM indisponible */ }
}

// Échap et Tab ne doivent pas permettre de contourner la modale ni d'atteindre
// l'interface située derrière (une caisse reste utilisable au clavier seul).
function bloquerClavier(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopPropagation();
        return;
    }
    if (e.key === 'Tab') {
        const bouton = document.getElementById(ID_RACINE + '-btn');
        if (bouton) {
            e.preventDefault();
            bouton.focus();
        }
    }
}

/**
 * Affiche la modale. Idempotent : un second appel (vérification périodique
 * suivante) ne recrée rien.
 *
 * @param {object} infos          { versionBundle, versionDeployee, buildTime }
 * @param {number} echecsPrecedents Tentatives infructueuses sur cette version.
 */
function afficherGateMiseAJour(infos, echecsPrecedents = 0) {
    if (affichee || document.getElementById(ID_RACINE)) return;
    affichee = true;

    const racine = document.createElement('div');
    racine.id = ID_RACINE;
    racine.setAttribute('role', 'alertdialog');
    racine.setAttribute('aria-modal', 'true');
    racine.style.cssText = [
        'position:fixed', 'inset:0', 'top:0', 'right:0', 'bottom:0', 'left:0',
        'background:rgba(15,23,42,.72)',
        'backdrop-filter:blur(2px)',
        // Au-dessus de tout : SweetAlert2 monte à 1060, les modales Bootstrap
        // à 1050. Rien ne doit pouvoir passer devant cette modale.
        'z-index:2147483647',
        'display:flex', 'align-items:center', 'justify-content:center',
        'padding:16px',
        'font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif',
    ].join(';');

    // Avertissement d'échec : n'apparaît qu'à partir de la 2ᵉ tentative sur la
    // même version, signe que le service worker ou un cache serveur résiste.
    const blocEchec = echecsPrecedents > 0 ? `
        <div style="margin:0 0 16px;padding:12px 14px;border-radius:8px;
                    background:#FEF3C7;border:1px solid #FCD34D;color:#92400E;
                    font-size:13px;line-height:1.5;text-align:left;">
          <b>Tentative précédente sans effet</b><br>
          L'application n'a pas pu être mise à jour lors ${echecsPrecedents > 1
              ? `des ${echecsPrecedents} dernières tentatives`
              : 'de la dernière tentative'}.
          Réessayez ; si le message revient, contactez le support technique.
        </div>` : '';

    racine.innerHTML = `
      <div style="background:#fff;border-radius:14px;max-width:440px;width:100%;
                  padding:28px 26px 24px;text-align:center;
                  box-shadow:0 24px 60px rgba(0,0,0,.35);">

        <div style="width:60px;height:60px;margin:0 auto 18px;border-radius:50%;
                    background:${COULEUR}1A;display:flex;align-items:center;
                    justify-content:center;">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none"
               stroke="${COULEUR}" stroke-width="2" stroke-linecap="round"
               stroke-linejoin="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-3-6.7"></path>
            <polyline points="21 3 21 9 15 9"></polyline>
          </svg>
        </div>

        <h2 style="margin:0 0 10px;font-size:19px;font-weight:600;color:#0F172A;">
          Nouvelle version disponible
        </h2>

        <p style="margin:0 0 18px;font-size:14px;line-height:1.55;color:#475569;">
          Une mise à jour de l'application a été déployée.
          Son installation est nécessaire pour continuer.
        </p>

        ${blocEchec}

        <div style="display:flex;align-items:center;justify-content:center;gap:10px;
                    margin:0 0 20px;font-size:13px;color:#64748B;">
          <span style="padding:4px 10px;border-radius:6px;background:#F1F5F9;">
            ${infos.versionBundle}
          </span>
          <span aria-hidden="true">→</span>
          <span style="padding:4px 10px;border-radius:6px;background:${COULEUR}1A;
                       color:${COULEUR};font-weight:600;">
            ${infos.versionDeployee}
          </span>
        </div>

        <button id="${ID_RACINE}-btn" type="button"
                style="width:100%;padding:13px 18px;border:0;border-radius:9px;
                       background:${COULEUR};color:#fff;font-size:15px;
                       font-weight:600;cursor:pointer;font-family:inherit;">
          Installer
        </button>

        <p id="${ID_RACINE}-note"
           style="margin:14px 0 0;font-size:12px;line-height:1.5;color:#94A3B8;">
          Vos données enregistrées sur ce poste, y compris les ventes en attente
          de synchronisation, sont conservées.
        </p>
      </div>
    `;

    document.body.appendChild(racine);
    empecherInteractionArrierePlan(true);
    document.addEventListener('keydown', bloquerClavier, true);

    const bouton = document.getElementById(ID_RACINE + '-btn');
    const note = document.getElementById(ID_RACINE + '-note');
    bouton.focus();

    bouton.addEventListener('click', async () => {
        // Un double clic relancerait une seconde purge pendant la première.
        bouton.disabled = true;
        bouton.style.opacity = '.65';
        bouton.style.cursor = 'default';
        bouton.textContent = 'Installation en cours…';
        note.textContent = 'Ne fermez pas cette fenêtre, l\'application va redémarrer.';

        try {
            await appliquerMiseAJour(infos.versionDeployee);
        } catch (e) {
            // appliquerMiseAJour ne rend normalement pas la main (la page est
            // remplacée). Si on arrive ici, le rechargement lui-même a échoué.
            console.error('[version] Installation échouée', e);
            bouton.disabled = false;
            bouton.style.opacity = '1';
            bouton.style.cursor = 'pointer';
            bouton.textContent = 'Réessayer';
            note.textContent = 'L\'installation n\'a pas abouti. Réessayez.';
        }
    });
}

export { afficherGateMiseAJour };
